import { Location, HotelRule, HotelApplicability, HotelChainsFamily, HotelPropertyCodesFamily } from './hotel-rule';
import { HotelRuleSearchCriteriaUi, HotelRuleSearchCriteria } from './hotel-rule-search-criteria';
import {
  RULE_STATUS_ALL,
  RULE_STATUS_ACTIVE,
  ALL_ACTION,
  HotelRuleUi,
  EXCLUDE_ACTION,
  LIGHT_EXCLUDE_ACTION,
  COMMISSIONABLE_ALL,
  COMMISSIONABLE_YES,
  HotelApplicabilityUi,
  SOURCE_TYPE_GDS,
  SOURCE_TYPE_AGGREGATOR,
  COMMISSIONABLE_NO
} from './hotel-rule-ui';

export const convertToHotelRule = (hotelRuleUi: HotelRuleUi, hotelsV2: boolean | null): HotelRule => {
  const hotelRule = convertToBasicHotelRule(hotelRuleUi, hotelsV2);
  if (hotelRuleUi.id && hotelRuleUi.id.length > 0) {
    hotelRule.id = hotelRuleUi.id;
  }
  if (hotelRuleUi.version) {
    hotelRule.version = hotelRuleUi.version;
  }
  if (hotelRuleUi.description && hotelRuleUi.description.length > 0) {
    hotelRule.description = hotelRuleUi.description;
  }

  if (hotelRuleUi.enableFullTextSearch) {
    hotelRule.enableFullTextSearch = true;
  }

  return hotelRule;
};

const convertToBasicHotelRule = (hotelRuleUi: HotelRuleUi, hotelsV2: boolean | null): HotelRule => {
  // Calculate final action:
  let hotelAction = hotelRuleUi.action;
  if (!hotelsV2 && hotelAction === EXCLUDE_ACTION && hotelRuleUi.lightExclude) {
    hotelAction = LIGHT_EXCLUDE_ACTION;
  }
  return {
    organization: hotelRuleUi.organization ?? '',
    name: hotelRuleUi.name,
    active: hotelRuleUi.active,
    action: hotelAction,
    hotelApplicability: convertToHotelApplicability(hotelRuleUi.hotelApplicability as HotelApplicabilityUi),
    weight: hotelRuleUi.weight
  };
};

export const convertToHotelRuleUi = (hotelRule: HotelRule, hotelsV2: boolean | null): HotelRuleUi => {
  let lightExclude = false;
  let hotelAction = hotelRule.action;
  if (!hotelsV2 && hotelAction === LIGHT_EXCLUDE_ACTION) {
    hotelAction = EXCLUDE_ACTION;
    lightExclude = true;
  }

  return {
    organization: hotelRule.organization,
    name: hotelRule.name,
    id: hotelRule.id,
    version: hotelRule.version,
    description: hotelRule.description,
    active: hotelRule.active,
    action: hotelAction,
    lightExclude,
    hotelApplicability: convertToHotelApplicabilityUi(hotelRule.hotelApplicability, hotelsV2),
    weight: hotelRule.weight
  };
};

export const convertToHotelSearchCriteria = (searchCriteriaUi: HotelRuleSearchCriteriaUi): HotelRuleSearchCriteria => {
  const searchCriteria: HotelRuleSearchCriteria = {
    id: searchCriteriaUi.id ? searchCriteriaUi.id : undefined,
    organization: searchCriteriaUi.organization
  };
  if (searchCriteriaUi.name !== undefined && searchCriteriaUi.name.length > 0) {
    searchCriteria.name = searchCriteriaUi.name;
  }
  if (searchCriteriaUi.commissionable !== undefined && searchCriteriaUi.commissionable !== COMMISSIONABLE_ALL) {
    searchCriteria.commissionable = searchCriteriaUi.commissionable === COMMISSIONABLE_YES;
  }
  if (searchCriteriaUi.ruleStatus !== undefined && searchCriteriaUi.ruleStatus !== RULE_STATUS_ALL) {
    searchCriteria.active = searchCriteriaUi.ruleStatus === RULE_STATUS_ACTIVE;
  }
  if (searchCriteriaUi.actionType !== undefined && searchCriteriaUi.actionType !== ALL_ACTION) {
    searchCriteria.action = searchCriteriaUi.actionType;
  }
  if (searchCriteriaUi.applicability !== undefined) {
    if (searchCriteriaUi.applicability.pointOfSale?.length) {
      searchCriteria.pointOfSale = { names: searchCriteriaUi.applicability.pointOfSale };
    }
    if (searchCriteriaUi.applicability.destination?.length) {
      searchCriteria.destination = { names: searchCriteriaUi.applicability.destination };
    }
  }
  if (searchCriteriaUi.enableFullTextSearch) {
    searchCriteria.enableFullTextSearch = true;
  }

  return searchCriteria;
};

const convertToHotelChainsFamilies = (names?: string[]): HotelChainsFamily[] => names?.map((name) => ({ name })) ?? [];

const convertToHotelPropertyCodesFamilies = (names?: string[]): HotelPropertyCodesFamily[] =>
  names?.map((name) => ({ name })) ?? [];

const convertToHotelApplicability = (applicability: HotelApplicabilityUi): HotelApplicability => {
  convertPosAndMarketNamesForService(applicability);
  const hotelApplicability: HotelApplicability = {
    pointOfSales: applicability.pointOfSales,
    destinations: applicability.destinations,
    masterChains: applicability.masterChains,
    chains: applicability.chains,
    hotelChainsFamilies: convertToHotelChainsFamilies(applicability.hotelChainsFamilies),
    propertyCodes: applicability.propertyCodes,
    hotelPropertyCodesFamilies: convertToHotelPropertyCodesFamilies(applicability.hotelPropertyCodesFamilies),
    rateCodes: applicability.rateCodes,
    sourceTypes: buildListOfSourceTypes(applicability),
    rateFamilies: applicability.rateFamilies,
    validityApplicationWeekdays: applicability.validityApplicationWeekdays,
    validityApplicationPeriods: applicability.validityApplicationPeriods
  };

  if (applicability.commissionable && applicability.commissionable !== COMMISSIONABLE_ALL) {
    hotelApplicability.commissionable = convertCommissionableValueToBoolean(applicability);
  }
  return hotelApplicability;
};

const convertToHotelApplicabilityUi = (
  applicability: HotelApplicability,
  hotelsV2: boolean | null
): HotelApplicabilityUi => {
  const hotelApplicability = {
    pointOfSales: applicability.pointOfSales,
    destinations: applicability.destinations,
    masterChains: applicability.masterChains,
    chains: applicability.chains,
    hotelChainsFamilies: convertHotelChainsFamilies(applicability.hotelChainsFamilies),
    propertyCodes: applicability.propertyCodes,
    hotelPropertyCodesFamilies: convertHotelPropertyCodesFamilies(applicability.hotelPropertyCodesFamilies),
    rateCodes: applicability.rateCodes,
    sourceTypesGds: findSourceType(applicability.sourceTypes, SOURCE_TYPE_GDS),
    sourceTypesAggregator: findSourceType(applicability.sourceTypes, SOURCE_TYPE_AGGREGATOR),
    commissionable: convertCommissionableValue(applicability, hotelsV2),
    rateFamilies: applicability.rateFamilies,
    validityApplicationPeriods: applicability.validityApplicationPeriods,
    validityApplicationWeekdays: applicability.validityApplicationWeekdays
  };
  convertPosAndMarketNamesForUi(hotelApplicability);

  return hotelApplicability;
};

const convertHotelChainsFamilies = (hotelChainsFamilies?: HotelChainsFamily[]): string[] | undefined =>
  hotelChainsFamilies !== undefined
    ? hotelChainsFamilies.map((hotelChainsFamily) => hotelChainsFamily.name)
    : undefined;

const convertHotelPropertyCodesFamilies = (
  hotelPropertyCodesFamilies?: HotelPropertyCodesFamily[]
): string[] | undefined =>
  hotelPropertyCodesFamilies !== undefined
    ? hotelPropertyCodesFamilies.map((hotelPropertyCodesFamily) => hotelPropertyCodesFamily.name)
    : undefined;

const convertPosAndMarketNamesForService = (applicability: HotelApplicabilityUi) => {
  if (!applicability) {
    return;
  }
  if (applicability.pointOfSaleNames && applicability.pointOfSaleNames.length > 0) {
    const pointsOfSale: Location[] = [];
    applicability.pointOfSaleNames.forEach((posName) => pointsOfSale.push({ name: posName }));
    applicability.pointOfSales = pointsOfSale;
  }
  if (applicability.destinationNames && applicability.destinationNames.length > 0) {
    const destinations: Location[] = [];
    applicability.destinationNames.forEach((destinationName) => destinations.push({ name: destinationName }));
    applicability.destinations = destinations;
  }
};

const convertPosAndMarketNamesForUi = (applicability: HotelApplicabilityUi) => {
  if (!applicability) {
    return;
  }
  if (applicability.pointOfSales !== undefined && applicability.pointOfSales.length > 0) {
    const pointOfSaleNames: string[] = [];
    applicability.pointOfSales.forEach((pos) => pointOfSaleNames.push(pos.name));
    applicability.pointOfSaleNames = pointOfSaleNames;
  }
  if (applicability.destinations !== undefined && applicability.destinations.length > 0) {
    const destinationNames: string[] = [];
    applicability.destinations.forEach((destination) => destinationNames.push(destination.name));
    applicability.destinationNames = destinationNames;
  }
};

const buildListOfSourceTypes = (applicability: HotelApplicabilityUi): string[] => {
  const sourceTypes = [];
  if (applicability.sourceTypesGds && applicability.sourceTypesGds === true) {
    sourceTypes.push(SOURCE_TYPE_GDS);
  }
  if (applicability.sourceTypesAggregator && applicability.sourceTypesAggregator === true) {
    sourceTypes.push(SOURCE_TYPE_AGGREGATOR);
  }
  return sourceTypes;
};

const convertCommissionableValueToBoolean = (applicability: HotelApplicabilityUi): boolean => {
  let commissionable = false;
  if (applicability.commissionable === COMMISSIONABLE_YES) {
    commissionable = true;
  }
  return commissionable;
};

const convertCommissionableValue = (
  applicability: HotelApplicability,
  hotelsV2?: boolean | null
): string | undefined => {
  if (applicability.commissionable === undefined) {
    return hotelsV2 ? undefined : COMMISSIONABLE_ALL;
  } else if (applicability.commissionable === true) {
    return COMMISSIONABLE_YES;
  }
  return COMMISSIONABLE_NO;
};

const findSourceType = (sourceTypes: string[] | undefined, typeToFind: string) => {
  if (sourceTypes && sourceTypes.length > 0) {
    return sourceTypes.includes(typeToFind);
  }
  return false;
};
